import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SEM_SERVIDOR, ehLojaDigital } from "@/lib/loja-digital";
import { Suspense, lazy, useEffect, useLayoutEffect, useState } from "react";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { StoreProvider, useStore } from "@/contexts/StoreContext";
import { logger } from "@/lib/logger";
import { PlayerAuthProvider } from "@/contexts/PlayerAuthContext";
import { carregarTemplate } from "@/templates/registro";
import { documentKind, type DocumentKind } from "@/code-theme/routes";
import { TemplateProvider } from "@/templates/TemplateContext";
import type { Template } from "@/templates/tipos";
import type { Store } from "@/lib/services/store.service";
import { SiteProvider, type Site } from "@/construtor/SiteContext";
import type { Lingua } from "@/construtor/documento-do-site";
import { ScrollToTop } from "@/components/common/ScrollToTop";import { DynamicFavicon } from '@/components/common/DynamicFavicon';import { StoreLoading } from "@/components/common/StoreLoading";
import { StoreNotFound } from "@/components/common/StoreNotFound";
import { AddonRoute } from "@/components/common/AddonRoute";
import { StoreSuspended } from "@/components/common/StoreSuspended";

// Pages
import HomePage from "@/pages/HomePage";
import StorePage from "@/pages/StorePage";
import ProductPage from "@/pages/ProductPage";
import { ComprarPeloLink } from "@/components/store/ComprarPeloLink";
import CartPage from "@/pages/CartPage";
import SupportPage from "@/pages/SupportPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import StaffTicketsPage from "@/pages/StaffTicketsPage";
import StaffTicketDetailPage from "@/pages/StaffTicketDetailPage";
import ProfilePage from "@/pages/ProfilePage";
import GiftsPage from "@/pages/GiftsPage";
import PlayersPage from "@/pages/PlayersPage";
import PartnerPage from "@/pages/PartnerPage";
import DiscordCallbackPage from "@/pages/DiscordCallbackPage";
import DiscordStartLinkPage from "@/pages/DiscordStartLinkPage";
import WikiPage from "@/pages/WikiPage";
import NoticiaPage from "@/pages/NoticiaPage";
import PublishNewsPage from '@/pages/PublishNewsPage'
import DownloadPage from "@/pages/DownloadPage";
import ChangelogPage from "@/pages/ChangelogPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import DataCollectionPage from "@/pages/DataCollectionPage";
import ServerRulesPage from "@/pages/ServerRulesPage";
import { PaymentSuccess } from "@/pages/PaymentSuccess";
import { PaymentFailure } from "@/pages/PaymentFailure";
import { PaymentPending } from "@/pages/PaymentPending";
import ForumPage from "@/pages/ForumPage";
import ForumBoardPage from "@/pages/ForumBoardPage";
import ForumTopicPage from "@/pages/ForumTopicPage";
import NewTopicPage from "@/pages/NewTopicPage";
import FormsPage from "@/pages/FormsPage";
import FormDetailPage from "@/pages/FormDetailPage";
import MySubmissionsPage from "@/pages/MySubmissionsPage";
import MySubmissionDetailPage from "@/pages/MySubmissionDetailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SignInPage from "@/pages/SignInPage";

// O editor do construtor e a prévia do rascunho: só baixam para quem abre essas rotas.
const ModoConstrutor = lazy(() => import("@/construtor/editor/ModoConstrutor"));
const ModoPrevia = lazy(() => import("@/construtor/editor/ModoPrevia"));
const ThemePreviewMode = lazy(() => import("@/templates/code/PreviewMode"));

/** A rota é do construtor (`/__construtor`) ou de um link de prévia (`/__previa/<token>`)? */
function rotaEspecial(): 'construtor' | 'previa' | 'theme-preview' | null {
  const caminho = window.location.pathname;
  if (caminho === '/__construtor' || caminho.indexOf('/__construtor/') === 0) return 'construtor';
  if (caminho.indexOf('/__previa/') === 0) return 'previa';
  if (caminho.indexOf('/__theme-preview/') === 0) return 'theme-preview';
  return null;
}

/**
 * Extrai o domínio completo da URL atual
 * Exemplos:
 * - loja1.hystore.com.br → loja1.hystore.com.br
 * - minhaloja.com → minhaloja.com
 * - localhost:3000 → localhost (modo dev)
 */
function extractDomain(): string {
  const hostname = window.location.hostname;
  
  // Desenvolvimento local puro "localhost"
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_DEFAULT_STORE_DOMAIN || 'demo.hystore.com.br';
  }
  
  // Se for "teste.localhost", mantemos "teste" e colamos no domínio do .env (ex: hystore.com.br)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.split('.')[0];
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'hystore.com.br';
    return `${subdomain}.${baseDomain}`;
  }
  
  // Produção: retorna só o hostname, matando a porta (nunca usar .host)
  return hostname;
}

function AppContent() {
  const { store, loading, error, loadStoreByDomain } = useStore();

  useEffect(() => {
    // Detectar domínio e carregar loja
    const domain = extractDomain();
    logger.log('🌐 Detected domain:', domain);
    loadStoreByDomain(domain);
  }, []);

  // Loading state
  if (loading) {
    return <StoreLoading />;
  }

  // Error state
  if (error || !store) {
    return <StoreNotFound error={error} />;
  }

  // Check if store is suspended or inactive
  if (store.isSuspended || !store.isActive) {
    return (
      <StoreSuspended
        storeName={store.name}
        suspensionReason={store.suspensionReason ?? undefined}
        scheduledDeletionAt={store.scheduledDeletionAt ?? undefined}
      />
    );
  }

  // Store loaded successfully
  logger.log('✅ Store loaded:', store.name);

  const especial = rotaEspecial();
  if (especial) {
    return (
      <Suspense fallback={<StoreLoading />}>
        {especial === 'construtor' ? <ModoConstrutor store={store} /> : especial === 'theme-preview' ? <ThemePreviewMode store={store} /> : <ModoPrevia store={store} />}
      </Suspense>
    );
  }

  return <LojaComTemplate store={store} />;
}

/**
 * A loja já carregada, desenhada com o template que o lojista escolheu.
 *
 * O template baixa à parte (cada um é um pedaço do build); enquanto isso fica
 * a mesma tela de carregamento da loja. Uma página que o template não troca
 * usa a de `src/pages`, já com as cores e fontes dele.
 */
function LojaComTemplate({ store }: { store: Store }) {
  const [template, setTemplate] = useState<Template | null>(null);
  // Loja em tema em código: carrinho, pagamento e conta abrem sem o tema (nem baixam o tema).
  const accountWithoutTheme = store.template === 'code' && documentKind(window.location.pathname) === 'account';

  useEffect(() => {
    let ativo = true;
    carregarTemplate(accountWithoutTheme ? 'classico' : store.template, { id: store.id }).then((carregado) => {
      if (ativo) setTemplate(carregado);
    });
    return () => {
      ativo = false;
    };
  }, [store.template, store.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!template) {
    return <StoreLoading />;
  }

  const boundary: DocumentKind | undefined = accountWithoutTheme ? 'account' : template.id === 'code' ? 'storefront' : undefined;
  return <LojaMontada store={store} template={template} boundary={boundary} />;
}

/**
 * Num tema em código, a página do tema e as páginas de conta e dinheiro nunca
 * dividem o mesmo documento: quando a rota troca de lado, a página recarrega
 * (o endereço já é o novo), antes de desenhar.
 */
function DocumentBoundary({ kind }: { kind: DocumentKind }) {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (documentKind(pathname) !== kind) window.location.reload();
  }, [pathname, kind]);
  return null;
}

/**
 * A loja com o template já escolhido. Separada do carregamento porque o
 * editor do construtor e o link de prévia montam a loja com um documento que
 * não é o publicado (`ModoConstrutor`, `ModoPrevia`).
 */
/** O que só o editor passa: a página pedida, a rota de amostra, o bloco selecionado e o modo de cor. */
export type EdicaoDaLoja = Pick<Site, 'pagina' | 'rota' | 'marcador' | 'selecionado' | 'modo'>;

interface LojaMontadaProps {
  store: Store;
  template: Template;
  basename?: string;
  lingua?: Lingua;
  editor?: boolean;
  edicao?: EdicaoDaLoja;
  /** Só na loja em tema em código: de que lado da fronteira este documento está. */
  boundary?: DocumentKind;
}

export function LojaMontada({ store, template, basename, lingua, editor, edicao, boundary }: LojaMontadaProps) {
  const codeTheme = template.id === 'code';
  // No tema em código, entrar é uma página (documento novo, sem o tema), não o modal por cima do tema.
  const signInAsPage = useState(() =>
    codeTheme ? () => window.location.assign(`/entrar?next=${encodeURIComponent(window.location.pathname + window.location.search)}`) : undefined,
  )[0];

  const Layout = template.Layout;
  const Inicio = template.paginas.inicio ?? HomePage;
  const Loja = template.paginas.loja ?? StorePage;
  const Produto = template.paginas.produto ?? ProductPage;

  const loja = (
    <TemplateProvider template={template}>
    <ThemeProvider
      storeColors={store.colors}
      template={template}
      modoPadrao={template.modoPadrao ?? store.colorMode}
      modoForcado={edicao?.modo ? (edicao.modo === 'escuro' ? 'dark' : 'light') : undefined}
      storeId={store.id}
    >
      <AuthProvider openLogin={signInAsPage}>
        <PlayerAuthProvider>
          <CartProvider>
            <BrowserRouter basename={basename}>
              <DynamicFavicon />
              <ScrollToTop />
              {boundary && <DocumentBoundary kind={boundary} />}
              <Routes>
              <Route element={<Layout />}>
                {/* Home */}
                <Route path="/" element={<Inicio />} />
                
                {/* Store — fluxo: SERVIDOR → CATEGORIA → PRODUTO */}
                {/* Loja digital não tem servidor para escolher: vai direto ao catálogo. */}
                <Route path="/loja" element={ehLojaDigital(store) ? <Navigate to={`/loja/${SEM_SERVIDOR}`} replace /> : <Loja />} />
                <Route path="/loja/:serverId" element={<Loja />} />
                <Route path="/loja/:serverId/:categorySlug" element={<Loja />} />
                <Route path="/loja/:serverId/:categorySlug/produto/:productSlug" element={<ComprarPeloLink><Produto /></ComprarPeloLink>} />
                
                {/* Cart */}
                <Route path="/carrinho" element={<CartPage />} />
                <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
                <Route path="/entrar" element={<SignInPage />} />
                
                {/* Payment Result Pages */}
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/failure" element={<PaymentFailure />} />
                <Route path="/payment/pending" element={<PaymentPending />} />
                
                {/* Support */}
                <Route path="/suporte" element={<AddonRoute addon="support"><SupportPage /></AddonRoute>} />
                <Route path="/suporte/ticket/:ticketId" element={<AddonRoute addon="support"><TicketDetailPage /></AddonRoute>} />
                <Route path="/suporte/staff" element={<AddonRoute addon="support"><StaffTicketsPage /></AddonRoute>} />
                <Route path="/suporte/staff/ticket/:ticketId" element={<AddonRoute addon="support"><StaffTicketDetailPage /></AddonRoute>} />
                
                {/* Profile */}
                <Route path="/perfil" element={<ProfilePage />} />
                {/* Link curto para a entrega da loja digital (e-mail, avisos). */}
                <Route path="/minhas-compras" element={<Navigate to="/perfil?tab=library" replace />} />
                <Route path="/perfil/discord/callback" element={<AddonRoute addon="discord"><DiscordCallbackPage /></AddonRoute>} />
                <Route path="/discord/vincular" element={<AddonRoute addon="discord"><DiscordStartLinkPage /></AddonRoute>} />
                
                {/* Gifts */}
                <Route path="/presentes" element={<GiftsPage />} />
                <Route path="/jogadores" element={<PlayersPage />} />
                <Route path="/parceiro" element={<PartnerPage />} />
                
                {/* Wiki/Help */}
                <Route path="/wiki" element={<AddonRoute addon="wiki"><WikiPage /></AddonRoute>} />
                <Route path="/wiki/:categoryId" element={<AddonRoute addon="wiki"><WikiPage /></AddonRoute>} />
                <Route path="/wiki/:categoryId/:articleId" element={<AddonRoute addon="wiki"><WikiPage /></AddonRoute>} />
                
                {/* Notícia */}
                {/*
                  Plural e por slug: é o link que o launcher monta e o que um
                  buscador indexa. O singular fica como alias porque links com
                  UUID já circularam em Discord e anúncio fixado — quebrá-los
                  transformaria a melhoria numa página de erro.
                */}
                <Route path="/noticias/publicar" element={<PublishNewsPage />} />
                <Route path="/noticias/:slug" element={<NoticiaPage />} />
                <Route path="/noticia/:slug" element={<NoticiaPage />} />

                {/* Download do launcher */}
                <Route path="/download" element={<AddonRoute addon="launcher"><DownloadPage /></AddonRoute>} />
                <Route path="/changelog" element={<AddonRoute addon="launcher"><ChangelogPage /></AddonRoute>} />
                
                {/* Forum */}
                <Route path="/forum" element={<AddonRoute addon="forum"><ForumPage /></AddonRoute>} />
                <Route path="/forum/:categorySlug/:boardSlug" element={<AddonRoute addon="forum"><ForumBoardPage /></AddonRoute>} />
                <Route path="/forum/:categorySlug/:boardSlug/new-topic" element={<AddonRoute addon="forum"><NewTopicPage /></AddonRoute>} />
                <Route path="/forum/:categorySlug/:boardSlug/:topicSlug" element={<AddonRoute addon="forum"><ForumTopicPage /></AddonRoute>} />
                
                {/* Forms */}
                <Route path="/formularios" element={<AddonRoute addon="forms"><FormsPage /></AddonRoute>} />
                <Route path="/formularios/:slug" element={<AddonRoute addon="forms"><FormDetailPage /></AddonRoute>} />
                <Route path="/minhas-respostas" element={<AddonRoute addon="forms"><MySubmissionsPage /></AddonRoute>} />
                <Route path="/minhas-respostas/:submissionId" element={<AddonRoute addon="forms"><MySubmissionDetailPage /></AddonRoute>} />

                {/* Legal pages */}
                <Route path="/regras" element={<ServerRulesPage />} />
                <Route path="/termos" element={<TermsOfServicePage />} />
                <Route path="/privacidade" element={<PrivacyPolicyPage />} />
                <Route path="/coleta-de-dados" element={<DataCollectionPage />} />

                {/* Páginas próprias do construtor: quem desenha é a moldura do Personalizado. */}
                <Route path="/p/:slug" element={<></>} />

                {/* Só no tema em código: páginas que o tema escreve e o 404 (a moldura decide o que mostrar). */}
                {codeTheme && <Route path="/busca" element={<></>} />}
                {codeTheme && <Route path="/noticias" element={<></>} />}
                {codeTheme && <Route path="*" element={<></>} />}
              </Route>
            </Routes>
          </BrowserRouter>
        </CartProvider>
        </PlayerAuthProvider>
      </AuthProvider>
    </ThemeProvider>
    </TemplateProvider>
  );

  // O Personalizado desenha um documento: os blocos leem dele pelo SiteProvider.
  return template.documento ? <SiteProvider site={{ doc: template.documento, lingua: lingua ?? 'pt', editor: !!editor, ...edicao }}>{loja}</SiteProvider> : loja;
}

function App() {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  );
}

// Placeholder component for pages not fully implemented
interface PlaceholderPageProps {
  title: string;
}

function PlaceholderPage({ title }: PlaceholderPageProps) {
  return (
    <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="font-display text-4xl font-bold text-foreground mb-4">
          {title}
        </h1>
        <p className="text-muted-foreground">
          Esta página está em construção.
        </p>
      </div>
    </div>
  );
}

export default App;
